import React, { memo, useEffect, useRef } from 'react';
import { Send } from 'lucide-react';

/**
 * MessageInput Component
 * Memoized message input field to prevent unnecessary re-renders
 * Only re-renders when value, disabled, or theme props actually change
 */
const MessageInput = memo(({ 
  value, 
  onChange, 
  onSend, 
  disabled = false,
  theme = {},
  colors = {},
  onFocus
}) => {
  const inputRef = useRef(null);
  // Use sessionStorage to persist focus state across remounts (React.StrictMode)
  const FOCUS_KEY = 'messageInputWasFocused';

  // Maintain focus across remounts (React.StrictMode double-mount in dev)
  useEffect(() => {
    const wasFocused = sessionStorage.getItem(FOCUS_KEY) === 'true';
    if (wasFocused && inputRef.current && !disabled) {
      // Use requestAnimationFrame for better timing with React's rendering
      const rafId = requestAnimationFrame(() => {
        // Double-check after RAF
        const rafId2 = requestAnimationFrame(() => {
          if (inputRef.current && !disabled) {
            inputRef.current.focus();
            // Restore cursor position at end of input
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
            // Ensure it's actually focused
            if (document.activeElement !== inputRef.current) {
              inputRef.current.focus();
            }
          }
        });
        return () => cancelAnimationFrame(rafId2);
      });
      return () => cancelAnimationFrame(rafId);
    }
  });

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !disabled && value.trim()) {
      e.preventDefault();
      onSend();
    }
  };

  const handleChange = (e) => {
    onChange(e);
  };

  const handleFocus = (e) => {
    sessionStorage.setItem(FOCUS_KEY, 'true');
    if (e.target) {
      e.target.style.borderColor = 'rgba(59, 130, 246, 0.6)';
    }
    if (typeof onFocus === 'function') {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    // Only clear focus state if user actually blurred (not due to remount)
    // Use a small delay to check if focus returns
    setTimeout(() => {
      if (document.activeElement !== e.target && !e.target.matches(':focus')) {
        sessionStorage.setItem(FOCUS_KEY, 'false');
      }
    }, 100);
    if (e.target) {
      e.target.style.borderColor = 'rgba(59, 130, 246, 0.2)';
    }
  };

  return (
    <div style={{
      padding: '20px 24px',
      borderTop: '1px solid rgba(59, 130, 246, 0.2)',
      background: 'rgba(15, 20, 25, 0.5)'
    }}>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
          disabled={disabled}
          autoFocus={false}
          style={{
            flex: 1,
            padding: '12px 16px',
            background: 'rgba(15, 20, 25, 0.8)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '12px',
            color: theme.text,
            fontSize: '14px',
            outline: 'none',
            transition: 'border-color 0.2s ease',
            cursor: disabled ? 'not-allowed' : 'text',
            opacity: disabled ? 0.6 : 1
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <button
          onClick={onSend}
          disabled={disabled || !value.trim()}
          style={{
            padding: '12px 24px',
            background: value.trim() && !disabled 
              ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)` 
              : 'rgba(59, 130, 246, 0.1)',
            border: 'none',
            borderRadius: '12px',
            color: theme.text,
            fontSize: '14px',
            fontWeight: '600',
            cursor: value.trim() && !disabled ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            opacity: value.trim() && !disabled ? 1 : 0.5
          }}
        >
          <Send size={18} />
          Send
        </button>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function - only re-render if these props change
  // onChange and onSend are memoized with useCallback, so they should be stable
  return (
    prevProps.value === nextProps.value &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.onSend === nextProps.onSend &&
    prevProps.onFocus === nextProps.onFocus &&
    prevProps.theme?.text === nextProps.theme?.text &&
    prevProps.colors?.primary === nextProps.colors?.primary &&
    prevProps.colors?.secondary === nextProps.colors?.secondary
  );
});

MessageInput.displayName = 'MessageInput';

export default MessageInput;

